import { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { postRequest } from "../../utils/api";
import { saveToken } from "../../utils/auth";

const loginSchema = yup.object().shape({
    username: yup.string().required("Email address is required"),
    password: yup.string().required("Password is required"),
});

const Login = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const { register, handleSubmit, formState: { errors } } = useForm({
        resolver: yupResolver(loginSchema),
    });

    const onSubmit = async (data: any) => {
        setLoading(true);
        try {
            const response = await postRequest('/login', data);
            if (response.status === 'success') {
                saveToken(response.admin.user_admin_token);
                toast.success("Login successful!");
                navigate("/dashboard");
            } else {
                toast.error(response.message || "Login failed");
            }
        } catch (error: any) {
            toast.error(error.message || "An error occurred");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="hk-wrapper hk-pg-auth" data-footer="simple">
            <div className="hk-pg-wrapper py-0">
                <div className="hk-pg-body py-0" style={{background:'#fff'}}>
                    <div className="container-fluid">
                        <div className="row auth-split">
                            <div className="col-xl-5 col-lg-6 col-md-7 position-relative mx-auto">
                                <div className="auth-content flex-column pt-6 pb-md-6 pb-13">
                                    <div className="text-center mb-7">
                                        <a className="navbar-brand me-0" href="#">
                                            <img className="brand-img d-inline-block w-sm-30" src="logo.png" alt="brand"/>
                                        </a>
                                    </div>
                                    <form className="w-100" onSubmit={handleSubmit(onSubmit)}>
                                        <div className="row">
                                            <div className="col-xl-8 col-sm-10 mx-auto">
                                                <div className="text-center mb-4">
                                                    <h4>Sign in to your account</h4>
                                                    <p>Access your admin dashboard to manage operations</p>
                                                </div>
                                                <div className="row gx-3">
                                                    {/* Email Field */}
                                                    <div className="form-group col-lg-12 mb-3">
                                                        <div className="form-label-group">
                                                            <label>Email address</label>
                                                        </div>
                                                        <input 
                                                            className={`form-control ${errors.username ? "is-invalid" : ""}`} 
                                                            placeholder="Enter email or username" 
                                                            type="text"
                                                            {...register("username")}
                                                        />
                                                        {errors.username && <div className="invalid-feedback">{errors.username.message}</div>}
                                                    </div>

                                                    {/* Password Field */}
                                                    <div className="form-group col-lg-12 mb-3">
                                                        <div className="form-label-group">
                                                            <label>Password</label>
                                                        </div>
                                                        <div className="input-group password-check">
                                                            <span className="input-affix-wrapper">
                                                                <input 
                                                                    className={`form-control ${errors.password ? "is-invalid" : ""}`} 
                                                                    placeholder="Enter password" 
                                                                    type={showPassword ? "text" : "password"}
                                                                    {...register("password")}
                                                                />
                                                                <a 
                                                                    href="#!" 
                                                                    onClick={(e) => { 
                                                                        e.preventDefault(); 
                                                                        setShowPassword(!showPassword); 
                                                                    }}
                                                                    className="input-suffix text-muted"
                                                                    style={{ cursor: 'pointer' }}
                                                                >
                                                                    {/* Remix Icon Logic */}
                                                                    {showPassword ? (
                                                                        <i className="ri-eye-off-line"></i>
                                                                    ) : (
                                                                        <i className="ri-eye-line"></i>
                                                                    )}
                                                                </a>
                                                            </span>
                                                            {errors.password && <div className="invalid-feedback d-block">{errors.password.message}</div>}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="d-flex justify-content-center mt-2">
                                                    <div className="form-check form-check-sm mb-3">
                                                        <input type="checkbox" className="form-check-input" id="logged_in" defaultChecked />
                                                        <label className="form-check-label text-muted fs-7" htmlFor="logged_in">Keep me logged in</label>
                                                    </div>
                                                </div>

                                                <button 
                                                    type="submit" 
                                                    className="btn btn-primary btn-uppercase btn-block w-100 btn-lg" 
                                                    disabled={loading}
                                                >
                                                    {loading ? <img src='/dist/myloder.gif' alt="loading" width="20" /> : "Login"}
                                                </button>
                                            </div>
                                        </div>
                                    </form>
                                </div>

                                <div className="hk-footer border-0">
                                    <footer className="container-xxl footer">
                                        <div className="row">
                                            <div className="col-xl-8 text-center">
                                                <p className="footer-text pb-0">
                                                    <span className="copy-text">UOMOX © 2026-27 All rights reserved.</span> 
                                                    <a href="#" className="ms-2">Privacy Policy</a>
                                                    <span className="footer-link-sep mx-2">|</span>
                                                    <a href="#">T&C</a>
                                                </p>
                                            </div>
                                        </div>
                                    </footer>
                                </div>
                            </div>

                            {/* Side Image Column */}
                            <div className="col-xl-7 col-lg-6 col-md-5 col-sm-10 d-md-block d-none position-relative bg-primary-light-5">
                                <div className="auth-content flex-column text-center py-8">
                                    <img src="/dist/img/macaroni-logged-out.png" className="img-fluid w-sm-50" alt="login" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;